// Avisos na tela: conquista desbloqueada e aumento da sequência de dias (10 min de estudo no dia).
// Fica montado no App. Na primeira vez em cada aparelho só "anota" o que a pessoa já tem, sem avisar.
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useIsMobile, useTheme } from "../lib/theme";
import { loadLS, saveLS } from "../lib/storage";
import { BADGES } from "../lib/constants";
import { calcBadges } from "./badges";
import { useStudyDays, computeStreak, badgeExtra } from "../lib/stats";
import { fetchNoteCounts } from "../lib/noteStats";
import { localDayKey } from "../lib/dates";

const NOTE_SCREENS = ["resumos", "flashcards"];

export function AchievementWatcher({ uid, isAdmin, ready, timerStudiedSec, simSaved, questEntries, screen }) {
  const t = useTheme(), isMob = useIsMobile();
  const sd = useStudyDays(ready ? uid : null, !!isAdmin);
  const [nc, setNc] = useState(undefined);
  const [live, setLive] = useState({});             // segundos estudados nesta sessão do app, por dia
  const [toasts, setToasts] = useState([]);
  const prevDone = useRef(null);
  const seen = useRef(null);
  const lastScreen = useRef(screen);

  const loadCounts = useCallback(() => { if (uid) fetchNoteCounts(uid).then(x => x && setNc(x)); }, [uid]);
  useEffect(() => { setNc(undefined); if (ready) loadCounts(); }, [ready, loadCounts]);
  // Resumos e flashcards: confere de novo ao sair dessas telas
  useEffect(() => {
    if (NOTE_SCREENS.includes(lastScreen.current) && screen !== lastScreen.current) loadCounts();
    lastScreen.current = screen;
  }, [screen, loadCounts]);
  // Tempo de estudo salvo (vem do App)
  useEffect(() => {
    const on = e => { const { sec, at } = e.detail || {}; if (!(sec > 0)) return; const k = localDayKey(at ? new Date(at) : new Date()); setLive(l => ({ ...l, [k]: (l[k] || 0) + sec })); };
    window.addEventListener("g17-study-added", on);
    return () => window.removeEventListener("g17-study-added", on);
  }, []);

  const byDay = useMemo(() => {
    const b = { ...(sd.byDay || {}) };
    Object.entries(live).forEach(([k, s]) => { b[k] = (b[k] || 0) + s; });
    return b;
  }, [sd.byDay, live]);
  const streak = useMemo(() => computeStreak(byDay), [byDay]);
  const unlocked = useMemo(() => {
    if (!ready || !sd.loaded || !nc) return null;
    const b = badgeExtra({ ...sd, byDay }, !!isAdmin, timerStudiedSec);
    return calcBadges(b.totalSec, simSaved || [], questEntries || [], { ...(b.extra || {}), nc });
  }, [ready, sd, byDay, nc, isAdmin, timerStudiedSec, simSaved, questEntries]);

  const push = useCallback(list => {
    if (!list.length) return;
    const items = list.map(x => ({ ...x, id: Math.random().toString(36).slice(2) }));
    setToasts(cur => [...cur, ...items].slice(-4));
    items.forEach(x => setTimeout(() => setToasts(cur => cur.filter(y => y.id !== x.id)), 7000));
  }, []);

  useEffect(() => {
    if (!unlocked || !uid) return;
    const key = `sf_ach_${uid}`;
    if (seen.current == null) seen.current = new Set(loadLS(key, null) || (null));
    const first = loadLS(key, null) == null;
    const out = [];
    if (!first) {
      BADGES.filter(b => unlocked.has(b.id) && !seen.current.has(b.id)).forEach(b => out.push({ kind: "badge", icon: b.icon, title: b.label, sub: b.desc }));
      if (prevDone.current === false && streak.todayDone) out.push({ kind: "streak", icon: "🔥", title: streak.current === 1 ? "Sequência iniciada!" : `Sequência de ${streak.current} dias!`, sub: "Você estudou 10 minutos hoje." });
    }
    unlocked.forEach(id => seen.current.add(id));
    saveLS(key, [...seen.current]);
    prevDone.current = streak.todayDone;
    push(out);
  }, [unlocked, streak, uid, push]);

  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", top: isMob ? "calc(env(safe-area-inset-top) + 10px)" : 16, left: 0, right: 0, zIndex: 9995, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, pointerEvents: "none", padding: "0 12px" }}>
      <style>{"@keyframes g17-toast-in{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:none}}"}</style>
      {toasts.map(x => (
        <div key={x.id} role="status" onClick={() => setToasts(cur => cur.filter(y => y.id !== x.id))}
          style={{ pointerEvents: "auto", cursor: "pointer", width: "100%", maxWidth: 380, display: "flex", alignItems: "center", gap: "0.8rem", padding: "0.75rem 0.95rem", borderRadius: 14,
            background: t.bgCard, color: t.text, border: `1px solid ${x.kind === "streak" ? "#f59e0b88" : t.acBorder}`, boxShadow: "0 10px 32px rgba(0,0,0,0.4)", animation: "g17-toast-in .25s ease-out", fontFamily: t.fontBody }}>
          <span style={{ fontSize: "1.8rem", lineHeight: 1 }}>{x.icon}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: x.kind === "streak" ? "#f59e0b" : t.ac }}>{x.kind === "streak" ? "Sequência" : "Conquista desbloqueada"}</div>
            <div style={{ fontSize: "0.95rem", fontWeight: 800 }}>{x.title}</div>
            <div style={{ fontSize: "0.76rem", color: t.textDim }}>{x.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
